import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { ChevronDown, X, Search } from "lucide-react";
import { cn } from "@/lib/utils";

export function SearchableSelect({ 
  options = [], 
  value, 
  onValueChange, 
  placeholder = "Search or select...",
  className,
  disabled = false,
  darkMode = false,
  "data-testid": testId
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option => 
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onValueChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onValueChange("");
    setSearchTerm("");
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  return (
    <div ref={containerRef} className={cn("relative", className)} data-testid={testId}>
      <div 
        className={cn(
          "flex items-center border rounded-md cursor-pointer",
          darkMode 
            ? "bg-[#1a3d18] border-[#22c55e] text-white hover:border-[#4ade80]"
            : "bg-white border-slate-200 hover:border-slate-300",
          isOpen && (darkMode 
            ? "ring-2 ring-[#22c55e] ring-opacity-30 border-[#22c55e]"
            : "ring-2 ring-[#357c2a] ring-opacity-20 border-[#357c2a]"),
          disabled && "opacity-50 cursor-not-allowed"
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        <Search className={cn("h-4 w-4 ml-3 flex-shrink-0", darkMode ? "text-slate-400" : "text-slate-400")} />
        <Input
          ref={inputRef}
          value={isOpen ? searchTerm : (value || "")}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={value ? value : placeholder}
          disabled={disabled}
          className={cn(
            "border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 pl-2 h-9 text-sm",
            darkMode ? "bg-transparent text-white placeholder:text-slate-400" : ""
          )}
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className={cn("p-1 mr-1 rounded", darkMode ? "hover:bg-[#265324]" : "hover:bg-slate-100")}
          >
            <X className={cn("h-4 w-4", darkMode ? "text-slate-400" : "text-slate-400")} />
          </button>
        )}
        <ChevronDown className={cn(
          "h-4 w-4 mr-3 transition-transform flex-shrink-0",
          darkMode ? "text-slate-400" : "text-slate-400",
          isOpen && "transform rotate-180"
        )} />
      </div>

      {isOpen && (
        <div className={cn(
          "absolute z-50 w-full mt-1 border rounded-md shadow-lg max-h-[250px] overflow-y-auto",
          darkMode ? "bg-[#1a3d18] border-[#22c55e]" : "bg-white border-slate-200"
        )}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, idx) => (
              <div
                key={idx}
                className={cn(
                  "px-3 py-2 cursor-pointer text-sm",
                  darkMode 
                    ? cn("hover:bg-[#22c55e]/20 text-slate-200", value === option && "bg-[#22c55e]/30 text-[#4ade80] font-medium")
                    : cn("hover:bg-[#f0f7ef]", value === option && "bg-[#e8f5e6] text-[#357c2a] font-medium")
                )}
                onClick={() => handleSelect(option)}
              >
                {option}
              </div>
            ))
          ) : (
            <div className={cn("px-3 py-2 text-sm", darkMode ? "text-slate-500" : "text-slate-400")}>
              {searchTerm ? "No matches found" : "No options available"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
